import React, { useState, useEffect } from 'react';
import Button from './ui/Button';
import Input from './ui/Input';
import { apiService } from '../services/api';

interface Module {
  id: number;
  title: string;
  position: number;
  parent_id: number | null;
  level: string;
  published: number;
  extra: string;
  is_report: number;
  created_at: string;
  updated_at: string;
}

interface ModuleFormProps {
  module?: Module | null;
  onSubmit: (moduleData: Omit<Module, 'id'> & { id?: number, sub_id?: number | null }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const ModuleForm: React.FC<ModuleFormProps> = ({ module, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState<Omit<Module, 'id'> & { id?: number, sub_id?: number | null }>({
    title: '',
    position: 0,
    parent_id: null,
    sub_id: null,
    level: '1',
    published: 1,
    extra: 'none',
    is_report: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [parentModules, setParentModules] = useState<Module[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [subModules, setSubModules] = useState<Module[]>([]);

  useEffect(() => {
    if (module) {
      setFormData({
        id: module.id,
        title: module.title || '',
        position: module.position || 0,
        parent_id: module.parent_id || null,
        sub_id: null,
        level: module.level || '1',
        published: module.published !== undefined ? module.published : 1,
        extra: module.extra || 'none',
        is_report: module.is_report !== undefined ? module.is_report : 0,
        created_at: module.created_at || new Date().toISOString(),
        updated_at: module.updated_at || new Date().toISOString(),
      });
    }
  }, [module]);

  useEffect(() => {
    loadParentModules();
  }, []);

  useEffect(() => {
    if (formData.parent_id) {
      // Load sub modules for selected parent
      setSubModules(parentModules.filter(m => m.parent_id === formData.parent_id));
    } else {
      setSubModules([]);
    }
  }, [formData.parent_id, parentModules]);

  const loadParentModules = async () => {
    setLoadingParents(true);
    try {
      const response = await apiService.get<{ items: Module[] }>('modules', { per_page: 1000 });
      if (response.success) {
        // Filter out the current module if editing to prevent self-reference
        const availableParents = response.data.items.filter((m: Module) => 
          !module || m.id !== module.id
        );
        setParentModules(availableParents);
      }
    } catch (error) {
      console.error('Error loading parent modules:', error);
    } finally {
      setLoadingParents(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      let parent_id = null;
      if (formData.parent_id && !formData.sub_id) parent_id = formData.parent_id;
      if (formData.sub_id) parent_id = formData.sub_id;
      try {
        await onSubmit({ ...formData, parent_id });
      } catch (error: any) {
        // Show backend error message if available
        if (error && error.message) {
          setErrors({ form: error.message });
        } else {
          setErrors({ form: 'Unable to create module. Please try again.' });
        }
      }
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {module ? 'Edit Module' : 'Add New Module'}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title *
              </label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter module title"
                error={errors.title}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Position
              </label>
              <Input
                type="number"
                value={formData.position}
                onChange={(e) => handleChange('position', parseInt(e.target.value))}
                placeholder="Enter position"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Parent Module
              </label>
              <select
                value={formData.parent_id || ''}
                onChange={(e) => handleChange('parent_id', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                disabled={loadingParents}
              >
                <option value="">No Parent (Top Level)</option>
                {parentModules.filter(m => m.parent_id === null).map((parent) => (
                  <option key={parent.id} value={parent.id}>
                    {parent.title}
                  </option>
                ))}
              </select>
              {loadingParents && (
                <p className="text-sm text-gray-500 mt-1">Loading modules...</p>
              )}
            </div>

            {formData.parent_id && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sub Module
                </label>
                <select
                  value={formData.sub_id || ''}
                  onChange={(e) => handleChange('sub_id', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="">No Sub (Second Level)</option>
                  {subModules.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Extra
              </label>
              <select
                value={formData.extra}
                onChange={(e) => handleChange('extra', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="none">None</option>
                <option value="alert">Alert</option>
                <option value="dev">Development</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Is Report
              </label>
              <select
                value={formData.is_report}
                onChange={(e) => handleChange('is_report', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.published}
                onChange={(e) => handleChange('published', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value={1}>Published</option>
                <option value={0}>Unpublished</option>
              </select>
            </div>

            {errors.form && (
              <div className="text-red-600 text-sm mb-2">{errors.form}</div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
              >
                {module ? 'Update Module' : 'Create Module'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModuleForm; 